import { useMemo } from "react";
import moment from "moment";
import { TaskSummary } from "features/Resources/Task/models";
import { computeTaskStats } from "features/Resources/Task/helpers/stats";
import { ActionSummary } from "features/Resources/Shared/models";
import type { CheckpointAction } from "./helpers";

export function useTaskStats(tasks: TaskSummary[] | undefined) {
  // Recomputed each render but only changes at midnight — keeps oldestOverdueDays
  // fresh across day boundaries without invalidating the memo mid-day.
  const todayStart = moment().startOf("day").valueOf();
  return useMemo(() => {
    const stats = computeTaskStats(tasks);
    // Denominator = completed + open (excludes upcoming/not-started, removed, skipped)
    const activeTotal = stats.completedCount + stats.openCount;
    const oldestOverdueDays = stats.oldestOverdueDueOn
      ? Math.max(0, moment(todayStart).diff(moment(stats.oldestOverdueDueOn).startOf("day"), "days"))
      : null;
    return {
      completed: stats.completedCount,
      total: activeTotal,
      openCount: stats.openCount,
      overdueCount: stats.overdueCount,
      oldestOverdueDays,
    };
  }, [tasks, todayStart]);
}

export function useLastCheckpoint(actions: ActionSummary[] | undefined) {
  return useMemo(() => {
    if (!actions) return undefined;
    const checkpoints = actions
      .filter((a) => a.actionType === "checkpoint")
      .filter((a) => (a.action as CheckpointAction).evaluatedAt != null)
      .sort((a, b) => {
        const dateA = new Date((a.action as CheckpointAction).evaluatedAt!).getTime();
        const dateB = new Date((b.action as CheckpointAction).evaluatedAt!).getTime();
        return dateB - dateA;
      });
    return checkpoints[0];
  }, [actions]);
}
